<template>
  <div :class="shellClass" :style="shellStyle">

    <!-- TOOLBAR -->
    <div class="table-corporate-toolbar">

      <b-form
        v-if="showSearch"
        class="table-corporate-search"
        autocomplete="off"
        @submit.prevent
      >
        <b-form-input
          ref="searchInput"
          v-model.trim="tableFilter"
          :placeholder="effectiveLabels.searchPlaceholder"
          type="search"
          name="table_search"
          autocomplete="off"
          data-lpignore="true"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          size="sm"
          :disabled="toolbarDisabled"
        ></b-form-input>
      </b-form>

      <div class="table-corporate-actions">

        <!-- BOTÓN VOLVER -->
        <b-button
          v-if="showBackButton"
          variant="primary"
          size="sm"
          class="table-toolbar-back-btn mr-2"
          @click="$emit('back')"
          :disabled="toolbarDisabled"
        >
          <i class="fa-solid fa-folder-open mr-1"></i>
          {{ backButtonText }}
        </b-button>

        <!-- BOTÓN EXPORTAR EXCEL -->
        <b-button
          v-if="showExportButton"
          variant="success"
          size="sm"
          class="table-toolbar-export-btn mr-2"
          @click="exportarExcel"
          :disabled="toolbarDisabled || items.length === 0 || loading"
        >
          <i class="fa-solid fa-file-excel mr-1"></i>
          {{ exportButtonText }}
        </b-button>

        <b-button
          v-if="hasAdvancedFilters"
          variant="outline-secondary"
          size="sm"
          class="mr-2"
          @click="showAdvancedFilters = !showAdvancedFilters"
          :disabled="toolbarDisabled"
        >
          <i class="fa-solid fa-filter mr-1"></i>
          {{ effectiveLabels.filters }}
        </b-button>

        <b-dropdown
          v-if="allowColumnToggle"
          variant="outline-secondary"
          size="sm"
          right
          class="table-columns-dropdown"
          :disabled="toolbarDisabled"
        >
          <template #button-content>
            <i class="fa-solid fa-table-columns mr-1"></i>
            {{ effectiveLabels.columns }}
          </template>

          <b-dropdown-item-button @click="showAllColumns">
            {{ effectiveLabels.showAllColumns }}
          </b-dropdown-item-button>

          <b-dropdown-divider></b-dropdown-divider>

          <div class="table-columns-list px-3 py-2">
            <b-form-checkbox
              v-for="col in columnOptions"
              :key="col.value"
              :checked="!hiddenColumns.includes(col.value)"
              @change="(checked) => toggleColumn(col.value, checked)"
            >
              {{ col.text }}
            </b-form-checkbox>
          </div>
        </b-dropdown>

        <div class="table-corporate-slot-actions">
          <slot name="toolbar-actions"></slot>
        </div>

      </div>
    </div>


    <!-- FILTROS AVANZADOS -->
    <div
      v-if="hasAdvancedFilters && showAdvancedFilters"
      class="table-corporate-filters"
    >
      <div class="table-filter-grid">

        <template v-for="def in filterDefs">

          <b-form-select
            v-if="def.type === 'select'"
            :key="`${filterValueKey(def)}_select`"
            v-model="filterValues[filterValueKey(def)]"
            :options="def.options || []"
            size="sm"
            :disabled="toolbarDisabled"
          ></b-form-select>

          <b-form-input
            v-else-if="def.type === 'date'"
            :key="`${filterValueKey(def)}_date`"
            v-model="filterValues[filterValueKey(def)]"
            type="date"
            size="sm"
            :disabled="toolbarDisabled"
          ></b-form-input>

          <b-form-input
            v-else
            :key="`${filterValueKey(def)}_text`"
            v-model.trim="filterValues[filterValueKey(def)]"
            :placeholder="def.placeholder || ''"
            size="sm"
            :disabled="toolbarDisabled"
          ></b-form-input>

        </template>

        <b-form-select
          v-if="enableSorting"
          v-model="sortBy"
          :options="sortFieldOptions"
          size="sm"
          :disabled="toolbarDisabled"
        ></b-form-select>

        <b-form-select
          v-if="enableSorting"
          v-model="sortDirectionValue"
          :options="sortDirectionOptions"
          size="sm"
          :disabled="toolbarDisabled"
        ></b-form-select>

      </div>

      <div class="mt-2 d-flex justify-content-end">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="clearFilters"
          :disabled="toolbarDisabled"
        >
          {{ effectiveLabels.clearFilters }}
        </b-button>
      </div>
    </div>


    <!-- CUERPO DE LA TABLA -->
    <div
      class="table-corporate-body"
      :style="tableBodyStyle"
    >

      <!-- ============================================================
           LOADING MEJORADO CON BARRA DE PROGRESO Y MENSAJES
           ============================================================ -->
      <div
        v-if="loading"
        class="table-corporate-loading-state"
      >
        <div class="table-corporate-empty-card loading-card">

          <!-- Spinner -->
          <div class="modern-spinner">
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
          </div>

          <!-- Mensaje rotativo -->
          <p class="mt-3 mb-1 loading-message">
            {{ currentLoadingMessage }}
          </p>

          <!-- Barra de progreso minimalista -->
          <div class="loading-progress-container">
            <div 
              class="loading-progress-bar" 
              :style="{ width: progressPercentage + '%' }"
            ></div>
          </div>

          <!-- Porcentaje -->
          <span class="loading-percentage">{{ progressPercentage }}%</span>

        </div>
      </div>


      <!-- TABLA -->
      <b-table
        v-if="filteredItems.length > 0 && !loading"
        :items="filteredItems"
        :fields="visibleFields"
        :per-page="showPagination ? perPage : filteredItems.length"
        :current-page="currentPage"
        :empty-text="currentEmptyText"
        :empty-filtered-text="effectiveLabels.noMatches"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="'last'"
        hover
        bordered
        responsive
        small
        class="tblEstandar tblCorporate"
        head-variant="custom-blue"
        @row-clicked="onRowClicked"
      >
        <template v-for="(_, slotName) in $slots" #[slotName]="slotData">
          <slot
            :name="slotName"
            v-bind="slotData"
          ></slot>
        </template>
      </b-table>


      <!-- MENSAJE CUANDO NO EXISTEN DATOS -->
      <div
        v-if="showInitialEmptyMessage && !loading"
        class="table-corporate-empty-state"
      >
        <div class="table-corporate-empty-card">

          <div class="table-corporate-empty-icon">
            <i class="fa-solid fa-table-list"></i>
          </div>

          <div class="table-corporate-empty-title">
            {{ effectiveLabels.initialEmptyTitle }}
          </div>

          <div class="table-corporate-empty-description">
            {{ effectiveLabels.initialEmptyMessage }}
          </div>

        </div>
      </div>

    </div>


    <!-- FOOTER -->
    <div
      v-if="showPagination && filteredItems.length > perPage"
      class="row col-12 mt-2 table-corporate-footer"
    >

      <div
        class="col-md-3 table-footer-block table-footer-left"
      ></div>

      <div
        class="col-md-6 d-flex justify-content-center align-items-center table-footer-block table-footer-center"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="filteredRows"
          :per-page="perPage"
          align="center"
          size="md"
          class="custom-pagination"
          first-number
          last-number
        ></b-pagination>
      </div>

      <div
        v-if="showRowsPerPage"
        class="col-md-3 d-flex justify-content-end align-items-center table-footer-block table-footer-right"
      >
        <label class="mr-2 mb-0">
          {{ effectiveLabels.rowsPerPage }}
        </label>

        <b-form-select
          v-model="perPage"
          :options="pageOptions"
          class="table-page-select"
          size="sm"
          style="width: 86px;"
        ></b-form-select>
      </div>

    </div>

  </div>
</template>


<script>
import * as XLSX from "exceljs"

const DEFAULT_LABELS = {
  noRow: "No hay registros",
  noMatches: "No se encontraron resultados",

  initialEmptyTitle: "Aún no hay información para mostrar",

  initialEmptyMessage:
    "Ingresa los filtros o datos necesarios para comenzar a visualizar la información.",

  loading: "Cargando...",

  searchPlaceholder: "Buscar en la tabla...",
  rowsPerPage: "Filas por página:",
  totalRows: "Registros visibles",
  noSort: "Sin orden",
  sortAsc: "A-Z / Menor a mayor",
  sortDesc: "Z-A / Mayor a menor",
  columns: "Columnas",
  showAllColumns: "Mostrar todas",
  filters: "Filtros",
  clearFilters: "Limpiar filtros",
  exportButton: "Exportar Excel"
}

// ============================================================
// MENSAJES ROTATIVOS PARA EL LOADING
// ============================================================
const LOADING_MESSAGES = {
  es: [
    'Cargando información...',
    'Preparando datos...',
    'Procesando solicitud...',
    'Esto puede tomar unos segundos...',
    'Casi listo...'
  ],
  en: [
    'Loading information...',
    'Preparing data...',
    'Processing request...',
    'This may take a few seconds...',
    'Almost ready...'
  ]
}


export default {
  name: 'CorporateDataTable',

  props: {
    items: {
      type: Array,
      default: () => []
    },

    loading: {
      type: Boolean,
      default: false
    },

    fields: {
      type: Array,
      default: () => []
    },

    filterDefs: {
      type: Array,
      default: () => []
    },

    searchKeys: {
      type: Array,
      default: null
    },

    labels: {
      type: Object,
      default: () => ({})
    },

    pageOptions: {
      type: Array,
      default: () => [5, 10, 20, 50, 100]
    },

    defaultPerPage: {
      type: Number,
      default: 10
    },

    allowColumnToggle: {
      type: Boolean,
      default: true
    },

    toolbarDisabled: {
      type: Boolean,
      default: false
    },

    enableSorting: {
      type: Boolean,
      default: true
    },

    showSearch: {
      type: Boolean,
      default: true
    },

    showPagination: {
      type: Boolean,
      default: true
    },

    showRowsPerPage: {
      type: Boolean,
      default: true
    },

    showBackButton: {
      type: Boolean,
      default: false
    },

    backButtonText: {
      type: String,
      default: 'Volver'
    },

    showExportButton: {
      type: Boolean,
      default: false
    },

    exportButtonText: {
      type: String,
      default: 'Exportar Excel'
    },

    exportReportTitle: {
      type: String,
      default: 'Reporte'
    },

    exportFileName: {
      type: String,
      default: null
    },

    exportSubtitle: {
      type: String,
      default: ''
    },

    exportFieldMapping: {
      type: Object,
      default: () => ({})
    },

    exportExcludeFields: {
      type: Array,
      default: () => ['accionEdit', 'accionDelete', 'accionVer']
    },

    exportFormatters: {
      type: Object,
      default: () => ({})
    },

    exportDecimals: {
      type: Number,
      default: 2
    },

    exportUseFiltered: {
      type: Boolean,
      default: true
    },

    exportIncludeColumns: {
      type: Array,
      default: () => []
    },

    narrow: {
      type: Boolean,
      default: true
    },

    maxWidth: {
      type: [String, Number],
      default: null
    },

    tableMinHeight: {
      type: [String, Number],
      default: 430
    },

    tableMaxHeight: {
      type: [String, Number],
      default: 430
    },

    // ============================================================
    // NUEVAS PROPS PARA EL LOADING MEJORADO
    // ============================================================

    // Tiempo estimado en segundos para completar el proceso
    estimatedTime: {
      type: Number,
      default: 40
    },

    // Mensajes personalizados (array de strings)
    customLoadingMessages: {
      type: Array,
      default: () => []
    },

    // Mostrar barra de progreso
    showProgressBar: {
      type: Boolean,
      default: true
    },

    // Mostrar porcentaje
    showPercentage: {
      type: Boolean,
      default: true
    }
  },

  emits: ['row-clicked', 'back', 'exporting', 'exported'],

  data() {
    return {
      perPage: this.defaultPerPage,
      currentPage: 1,
      tableFilter: '',
      showAdvancedFilters: false,
      hiddenColumns: [],
      filterValues: {},
      sortBy: null,
      sortDesc: false,
      exportando: false,

      // ============================================================
      // DATOS PARA EL LOADING MEJORADO
      // ============================================================
      progressPercentage: 0,
      currentMessageIndex: 0,
      messageInterval: null,
      progressInterval: null,
      startTime: null
    }
  },

  computed: {

    effectiveLabels() {
      return {
        ...DEFAULT_LABELS,
        ...this.labels
      }
    },

    // Obtener los mensajes según el idioma
    loadingMessages() {
      if (this.customLoadingMessages.length > 0) {
        return this.customLoadingMessages
      }

      const lang = this.$store?.state?._language || 'es-ES'
      const isSpanish = lang.startsWith('es')

      return isSpanish ? LOADING_MESSAGES.es : LOADING_MESSAGES.en
    },

    // Mensaje actual
    currentLoadingMessage() {
      return this.loadingMessages[this.currentMessageIndex] || this.effectiveLabels.loading
    },


    shellClass() {
      return {
        'table-corporate-shell': true,
        'table-corporate-shell-narrow': this.narrow
      }
    },


    shellStyle() {
      if (!this.maxWidth) return {}

      const value =
        typeof this.maxWidth === 'number'
          ? `${this.maxWidth}px`
          : this.maxWidth

      return {
        maxWidth: value
      }
    },


    tableBodyStyle() {
      const min =
        typeof this.tableMinHeight === 'number'
          ? `${this.tableMinHeight}px`
          : this.tableMinHeight

      const max =
        typeof this.tableMaxHeight === 'number'
          ? `${this.tableMaxHeight}px`
          : this.tableMaxHeight

      return {
        minHeight: min,
        maxHeight: max
      }
    },


    hasAdvancedFilters() {
      return this.filterDefs.length > 0 || this.enableSorting
    },


    visibleFields() {
      return this.fields.filter(
        (field) => !this.hiddenColumns.includes(field.key)
      )
    },


    columnOptions() {
      return this.fields.map((field) => ({
        value: field.key,
        text: field.label
      }))
    },


    effectiveSearchKeys() {
      if (this.searchKeys?.length) {
        return this.searchKeys
      }

      return this.fields.map(
        (field) => field.key
      )
    },


    filteredItems() {
      const search =
        this.normalize(this.tableFilter)

      return (this.items || []).filter((item) => {

        const matchesAdvanced =
          this.filterDefs.every(
            (def) => this.matchesFilter(item, def)
          )

        if (!matchesAdvanced) {
          return false
        }

        if (!search) {
          return true
        }

        return this.effectiveSearchKeys.some(
          (key) =>
            this
              .normalize(
                this.getItemValue(item, key)
              )
              .includes(search)
        )
      })
    },


    filteredRows() {
      return this.filteredItems.length
    },


    showInitialEmptyMessage() {
      return !this.items || this.items.length === 0
    },


    currentEmptyText() {
      if (this.showInitialEmptyMessage) {
        return ''
      }

      return this.effectiveLabels.noRow
    },


    sortFieldOptions() {
      const options = [
        {
          value: null,
          text: this.effectiveLabels.noSort
        }
      ]

      this.fields
        .filter(
          (field) => field.sortable !== false
        )
        .forEach((field) => {
          options.push({
            value: field.key,
            text: field.label
          })
        })

      return options
    },


    sortDirectionValue: {
      get() {
        return this.sortDesc
          ? 'desc'
          : 'asc'
      },

      set(value) {
        this.sortDesc = value === 'desc'
      }
    },


    sortDirectionOptions() {
      return [
        {
          value: 'asc',
          text: this.effectiveLabels.sortAsc
        },
        {
          value: 'desc',
          text: this.effectiveLabels.sortDesc
        }
      ]
    },

    // ============================================================
    // COMPUTED PARA EXPORTACIÓN
    // ============================================================

    datosParaExportar() {
      return this.exportUseFiltered
        ? this.filteredItems
        : this.items
    },

    columnasParaExportar() {
      if (this.exportIncludeColumns.length > 0) {
        return this.exportIncludeColumns
      }

      return this.visibleFields
        .map(f => f.key)
        .filter(key => !this.exportExcludeFields.includes(key))
    },

    nombreArchivoExport() {
      if (this.exportFileName) {
        return this.exportFileName
      }

      const ahora = new Date()
      const anio = ahora.getFullYear()
      const mes = String(ahora.getMonth() + 1).padStart(2, '0')
      const dia = String(ahora.getDate()).padStart(2, '0')
      const fechaStr = `${anio}-${mes}-${dia}`

      const tituloLimpio = this.exportReportTitle
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]/g, '_')

      return `${tituloLimpio}_${fechaStr}`
    }
  },


  watch: {

    perPage() {
      this.currentPage = 1
    },


    tableFilter() {
      this.currentPage = 1
    },


    filterValues: {
      deep: true,

      handler() {
        this.currentPage = 1
      }
    },


    filterDefs: {
      deep: true,
      immediate: true,

      handler() {

        const nextValues = {}

        this.filterDefs.forEach((def) => {

          const valueKey =
            this.filterValueKey(def)

          nextValues[valueKey] =
            this.filterValues[valueKey] ?? ''
        })

        this.filterValues =
          nextValues
      }
    },


    toolbarDisabled(value) {
      if (value) {
        this.showAdvancedFilters = false
      }
    },

    // ============================================================
    // WATCH PARA EL LOADING
    // ============================================================

    loading: {
      immediate: true,

      handler(newVal) {
        if (newVal) {
          this.startLoadingAnimation()
        } else {
          this.stopLoadingAnimation()
        }
      }
    }
  },


  methods: {

    // ============================================================
    // MÉTODOS DEL LOADING MEJORADO
    // ============================================================

    startLoadingAnimation() {
      // Reiniciar progreso
      this.progressPercentage = 0
      this.currentMessageIndex = 0
      this.startTime = Date.now()

      // Limpiar intervalos anteriores
      this.stopLoadingAnimation()

      // Intervalo para cambiar mensajes (cada 3 segundos)
      this.messageInterval = setInterval(() => {
        this.currentMessageIndex = (this.currentMessageIndex + 1) % this.loadingMessages.length
      }, 3000)

      // Intervalo para la barra de progreso
      // Simula progreso hasta llegar al 100% en el tiempo estimado
      const totalSteps = this.estimatedTime * 2 // Actualizar cada 0.5 segundos
      const stepIncrement = 100 / totalSteps

      this.progressInterval = setInterval(() => {
        // Calcular progreso basado en tiempo real
        const elapsed = (Date.now() - this.startTime) / 1000 // segundos
        const calculatedProgress = Math.min((elapsed / this.estimatedTime) * 100, 99)

        // Usar el máximo entre el cálculo y el incremento suave
        const newProgress = Math.min(
          Math.max(calculatedProgress, this.progressPercentage + stepIncrement),
          99
        )

        this.progressPercentage = Math.round(newProgress)

        // Si llegamos a 99%, mantenerlo hasta que termine el proceso
        if (this.progressPercentage >= 99) {
          // Dejamos que el componente padre decida cuando llegar al 100%
        }
      }, 500)
    },

    // Método para completar la barra (llamar cuando termine el proceso)
    completeLoading() {
      this.progressPercentage = 100
      // Opcional: cambiar al último mensaje
      this.currentMessageIndex = this.loadingMessages.length - 1

      // Detener intervalos después de un momento
      setTimeout(() => {
        this.stopLoadingAnimation()
      }, 500)
    },

    stopLoadingAnimation() {
      if (this.messageInterval) {
        clearInterval(this.messageInterval)
        this.messageInterval = null
      }

      if (this.progressInterval) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
    },

    // ============================================================
    // MÉTODOS EXISTENTES
    // ============================================================

    onRowClicked(
      item,
      index,
      event
    ) {
      this.$emit(
        'row-clicked',
        item,
        index,
        event
      )
    },


    showAllColumns() {
      this.hiddenColumns = []
    },


    toggleColumn(
      columnKey,
      checked
    ) {

      if (checked) {

        this.hiddenColumns =
          this.hiddenColumns.filter(
            (key) =>
              key !== columnKey
          )

        return
      }


      if (this.visibleFields.length === 1) {
        return
      }


      if (
        !this.hiddenColumns.includes(
          columnKey
        )
      ) {

        this.hiddenColumns.push(
          columnKey
        )
      }
    },


    clearFilters() {

      const reset = {}

      this.filterDefs.forEach((def) => {

        reset[
          this.filterValueKey(def)
        ] = ''
      })

      this.filterValues = reset
    },


    filterValueKey(def) {
      return def.id || def.key
    },


    getItemValue(
      item,
      key
    ) {

      if (!item || !key) {
        return ''
      }


      if (!key.includes('.')) {
        return item[key] ?? ''
      }


      return key
        .split('.')
        .reduce(
          (acc, part) =>
            acc?.[part] ?? '',
          item
        )
    },


    normalize(value) {
      return (value ?? '')
        .toString()
        .toLowerCase()
        .trim()
    },


    matchesFilter(
      item,
      def
    ) {

      const rawFilter =
        this.filterValues[
          this.filterValueKey(def)
        ]


      if (
        rawFilter === '' ||
        rawFilter === null ||
        rawFilter === undefined
      ) {
        return true
      }


      const itemKey =
        def.field || def.key


      const rawValue =
        this.getItemValue(
          item,
          itemKey
        )


      const mode =
        def.mode ||
        def.type ||
        'text'


      if (mode === 'dateFrom') {

        const value =
          (rawValue || '')
            .toString()
            .slice(0, 10)

        return value >= rawFilter
      }


      if (mode === 'dateTo') {

        const value =
          (rawValue || '')
            .toString()
            .slice(0, 10)

        return value <= rawFilter
      }


      if (
        mode === 'select' ||
        mode === 'equals'
      ) {
        return rawValue === rawFilter
      }


      return this
        .normalize(rawValue)
        .includes(
          this.normalize(rawFilter)
        )
    },

    // ============================================================
    // MÉTODOS DE EXPORTACIÓN
    // ============================================================

    async exportarExcel() {
      if (this.datosParaExportar.length === 0) {
        this.$toast?.warning('No hay datos para exportar')
        return
      }

      try {
        this.exportando = true
        this.$emit('exporting', { items: this.datosParaExportar })

        const datos = this.datosParaExportar
        const columnas = this.columnasParaExportar
        const datosFormateados = this.prepararDatosParaExcel(datos, columnas)

        await this.generarArchivoExcel(datosFormateados)

        this.$emit('exported', { items: datos })
        this.$toast?.success('Excel exportado exitosamente')

      } catch (error) {
        console.error('Error al exportar Excel:', error)
        this.$toast?.error('Error al exportar el Excel: ' + error.message)
      } finally {
        this.exportando = false
      }
    },

    prepararDatosParaExcel(datos, columnas) {
      return datos.map(item => {
        const fila = {}

        columnas.forEach(colKey => {
          let valor = this.getItemValue(item, colKey)

          if (this.exportFormatters[colKey]) {
            valor = this.exportFormatters[colKey](valor, item)
          }

          const nombreColumna = this.exportFieldMapping[colKey] || this.obtenerLabelPorKey(colKey)

          if (typeof valor === 'number' && !isNaN(valor)) {
            valor = Math.round(valor * Math.pow(10, this.exportDecimals)) / Math.pow(10, this.exportDecimals)
          }

          fila[nombreColumna] = valor
        })

        return fila
      })
    },

    obtenerLabelPorKey(key) {
      const field = this.fields.find(f => f.key === key)
      return field?.label || key
    },

    async generarArchivoExcel(datos) {
      const { Workbook } = XLSX
      const workbook = new Workbook()
      const worksheet = workbook.addWorksheet(this.exportReportTitle)

      const styles = {
        tituloPrincipal: {
          font: { name: "Calibri", size: 16, bold: true, color: { argb: "000000" } },
          alignment: { horizontal: "center", vertical: "middle" },
          fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } },
          border: {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          }
        },
        subtitulo: {
          font: { name: "Calibri", size: 11, bold: true, color: { argb: "000000" } },
          alignment: { horizontal: "center", vertical: "middle" },
          fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } },
          border: {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          }
        },
        encabezado: {
          font: { name: "Calibri", size: 11, bold: true, color: { argb: "000000" } },
          alignment: { horizontal: "center", vertical: "middle" },
          fill: { type: "pattern", pattern: "solid", fgColor: { argb: "F2F2F2" } },
          border: {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          }
        },
        datoNormal: {
          font: { name: "Calibri", size: 10, color: { argb: "000000" } },
          alignment: { horizontal: "left", vertical: "middle" },
          border: {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          }
        },
        datoCentrado: {
          font: { name: "Calibri", size: 10, color: { argb: "000000" } },
          alignment: { horizontal: "center", vertical: "middle" },
          border: {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          }
        },
        numeroNormal: {
          font: { name: "Calibri", size: 10, color: { argb: "000000" } },
          alignment: { horizontal: "right", vertical: "middle" },
          numFmt: `#,##0.${'0'.repeat(this.exportDecimals)}`,
          border: {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          }
        }
      }

      const headerColumns = Object.keys(datos[0] || {})
      const columnCount = headerColumns.length
      const lastCol = columnCount > 0 ? String.fromCharCode(64 + columnCount) : 'A'

      // Título
      worksheet.mergeCells(`A1:${lastCol}1`)
      worksheet.getCell('A1').value = this.exportReportTitle
      Object.assign(worksheet.getCell('A1'), styles.tituloPrincipal)

      // Subtítulo
      if (this.exportSubtitle) {
        worksheet.mergeCells(`A2:${lastCol}2`)
        worksheet.getCell('A2').value = this.exportSubtitle
        Object.assign(worksheet.getCell('A2'), styles.subtitulo)
      }

      // Fecha de generación
      const ahora = new Date()
      const fechaStr = ahora.toLocaleString()
      const usuario = this.$store?.state?.userName || 'Usuario'

      const rowIndexSubtitulo = this.exportSubtitle ? 3 : 2
      worksheet.mergeCells(`A${rowIndexSubtitulo}:${lastCol}${rowIndexSubtitulo}`)
      worksheet.getCell(`A${rowIndexSubtitulo}`).value = `Generado: ${fechaStr} por ${usuario}`
      Object.assign(worksheet.getCell(`A${rowIndexSubtitulo}`), styles.subtitulo)

      // Fila vacía
      const rowIndexData = rowIndexSubtitulo + 2
      worksheet.addRow([])

      // Encabezados
      const headerRow = worksheet.addRow(headerColumns)
      headerRow.eachCell((cell) => {
        Object.assign(cell, styles.encabezado)
      })

      // Anchos de columna
      const columnWidths = headerColumns.map(col => Math.max(col.length * 1.5, 12))
      worksheet.columns = columnWidths.map(width => ({ width: Math.min(width, 30) }))

      // Datos
      datos.forEach((fila) => {
        const valores = headerColumns.map(col => fila[col] ?? '')
        const currentRow = worksheet.addRow(valores)

        currentRow.eachCell((cell) => {
          const valor = cell.value
          const esNumero = typeof valor === 'number' && !isNaN(valor)

          if (esNumero) {
            Object.assign(cell, styles.numeroNormal)
          } else {
            const esFecha = typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}/.test(valor)
            if (esFecha) {
              Object.assign(cell, styles.datoCentrado)
            } else {
              Object.assign(cell, styles.datoNormal)
            }
          }
        })
      })

      // Congelar paneles
      const freezeRow = rowIndexData + 1
      worksheet.views = [
        { state: 'frozen', ySplit: freezeRow }
      ]

      // AutoFiltro
      worksheet.autoFilter = {
        from: { row: freezeRow, column: 1 },
        to: { row: freezeRow, column: columnCount }
      }

      // Generar y descargar
      const file = await workbook.xlsx.writeBuffer()
      const downloadLink = document.createElement("a")
      downloadLink.download = `${this.nombreArchivoExport}.xlsx`
      downloadLink.href = window.URL.createObjectURL(
        new Blob([file], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
      )
      downloadLink.style.display = "none"
      document.body.appendChild(downloadLink)
      downloadLink.click()
      downloadLink.remove()
    }
  },

  // ============================================================
  // LIMPIEZA AL DESTRUIR
  // ============================================================
  beforeDestroy() {
    this.stopLoadingAnimation()
  }
}
</script>


<style scoped>

/*
 * CONTENEDOR DEL CUERPO DE LA TABLA
 */
.table-corporate-body {
  position: relative;
}


/*
 * CONTENEDOR DEL ESTADO VACÍO
 */
.table-corporate-empty-state {
  position: absolute;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;

  align-items: center;
  justify-content: center;

  pointer-events: none;

  z-index: 10;

  padding: 20px;
}


/*
 * TARJETA MINIMALISTA
 */
.table-corporate-empty-card {
  width: 100%;
  max-width: 480px;

  padding: 28px 32px;

  text-align: center;

  background: #ffffff;

  border: 1px solid #e4e8ed;

  border-radius: 10px;

  box-shadow:
    0 3px 12px rgba(0, 0, 0, 0.06);
}

/* Tarjeta de loading con más espacio */
.loading-card {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}


/*
 * CÍRCULO DEL ÍCONO
 */
.table-corporate-empty-icon {
  width: 52px;
  height: 52px;

  margin: 0 auto 14px;

  display: flex;

  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: #f1f5f9;

  color: #54799f;

  font-size: 22px;
}


/*
 * TITULO
 */
.table-corporate-empty-title {
  margin-bottom: 8px;

  font-size: 18px;

  font-weight: 600;

  line-height: 1.35;

  color: #243447;
}


/*
 * DESCRIPCIÓN
 */
.table-corporate-empty-description {
  max-width: 390px;

  margin: 0 auto;

  font-size: 14px;

  font-weight: 400;

  line-height: 1.55;

  color: #6c757d;
}

/* ============================================================
   SPINNER MEJORADO
   ============================================================ */

.modern-spinner {
  position: relative;
  width: 60px;
  height: 60px;
  margin: 0 auto;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 4px solid transparent;
  animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
  border-top-color: #1f4f83;
  animation-delay: -0.45s;
}

.spinner-ring:nth-child(2) {
  border-right-color: #4a90d9;
  animation-delay: -0.3s;
}

.spinner-ring:nth-child(3) {
  border-bottom-color: #7fb3e6;
  animation-delay: -0.15s;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* ============================================================
   MENSAJE DE LOADING
   ============================================================ */

.loading-message {
  font-size: 15px;
  font-weight: 500;
  color: #243447;
  min-height: 24px;
  transition: opacity 0.3s ease;
}

/* ============================================================
   BARRA DE PROGRESO MINIMALISTA
   ============================================================ */

.loading-progress-container {
  width: 100%;
  max-width: 320px;
  height: 4px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 8px;
}

.loading-progress-bar {
  height: 100%;
  width: 0%;
  background: linear-gradient(90deg, #1f4f83, #4a90d9, #7fb3e6);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.loading-percentage {
  display: inline-block;
  margin-top: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
  min-width: 40px;
}

.table-corporate-loading-state {
  position: absolute;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 20;
  padding: 20px;
}

.table-corporate-loading-state .table-corporate-empty-card {
  min-height: 180px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* =========================================================
   LAYOUT DE LA TOOLBAR
   ========================================================= */

.table-corporate-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 6px 0;
}

.table-corporate-search {
  flex: 1 1 auto;
  min-width: 150px;
  max-width: 280px;
}

.table-corporate-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-left: auto;
}

.table-corporate-slot-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* =========================================================
   BOTÓN VOLVER
   ========================================================= */

.table-toolbar-back-btn {
  height: 31px;
  min-height: 31px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: 7px;
  font-size: 13px;
  background-color: #1f4f83 !important;
  color: white !important;
  border: 1px solid #1f4f83 !important;
  flex-shrink: 0;
}

.table-toolbar-back-btn:hover:not(:disabled) {
  background-color: #1a446f !important;
  border-color: #1a446f !important;
}

.table-toolbar-back-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* =========================================================
   BOTÓN EXPORTAR EXCEL
   ========================================================= */

.table-toolbar-export-btn {
  height: 31px;
  min-height: 31px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: 7px;
  font-size: 13px;
  background-color: #1f4f83 !important;
  color: white !important;
  border: 1px solid #1f4f83 !important;
  flex-shrink: 0;
}

.table-toolbar-export-btn:hover:not(:disabled) {
  background-color: #1f4f83 !important;
  border-color: #1f4f83 !important;
}

.table-toolbar-export-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* =========================================================
   RESPONSIVE
   ========================================================= */

@media (max-width: 768px) {
  .table-corporate-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .table-corporate-search {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 100%;
  }

  .table-corporate-actions {
    margin-left: 0;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .table-toolbar-back-btn,
  .table-toolbar-export-btn {
    width: auto;
    flex: 0 1 auto;
  }
}

@media (max-width: 576px) {
  .table-corporate-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .table-corporate-actions .btn,
  .table-corporate-actions .b-dropdown,
  .table-corporate-slot-actions {
    width: 100%;
  }

  .table-corporate-slot-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .table-toolbar-back-btn,
  .table-toolbar-export-btn {
    width: 100%;
    justify-content: center;
  }
}

</style>